import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdArrowBack,
  MdAssignment,
  MdContentCopy,
  MdDescription,
  MdEdit,
  MdImage,
  MdInsertDriveFile,
  MdOpenInNew,
  MdVerifiedUser,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  FileViewerModal,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import { ActiveBadge } from '../../master-data/MasterDataPage';
import { indentStatusBadge, operationalBadge } from '../../../../data/master/assetOwner';
import {
  findBuById,
  findSiteById,
} from '../../../../data/master/businessUnits';
import { findVillageById } from '../../../../data/master/villages';
import {
  lookupVillages,
  lookupBusinessUnits,
  lookupSites,
} from '../../../../repositories/lookup.repository';
import { listDocuments } from '../../../../repositories/document.repository';
import { listEquipment } from '../../../../repositories/equipment.repository';
import { listIndents } from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';
import {
  assignVendorBu,
  getVendor,
  listVendors,
  regenerateVendorPortalToken,
  revokeVendorBu,
} from '../../../../repositories/vendor.repository';

import { loadByIdOrCode } from '../../../../utils/entityResolve';
import { formatDobDisplay } from '../../../../utils/personName';
import { vendorPortalUrl } from '../../../../utils/vendorPortalToken';
import { IndentFormModal } from '../indents/IndentFormModal';
import { VendorAssetFormModal } from './VendorAssetFormModal';
import { VendorFormModal } from './VendorFormModal';
import { VendorVerificationStatusModal } from './VendorVerificationStatusModal';

const DETAIL_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 6 };
const DOC_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4 };

function DetailRow({ label, value }) {
  return (
    <Box
      p={{ base: 3, xl: 4 }}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      w="100%"
    >
      <Text
        fontSize={{ base: '12px', xl: '13px' }}
        color="text.secondary"
        mb={1}
        fontWeight="500"
      >
        {label}
      </Text>
      <Text
        fontSize={{ base: '15px', xl: '16px' }}
        fontWeight="600"
        whiteSpace="pre-wrap"
        lineHeight="1.4"
      >
        {value || '—'}
      </Text>
    </Box>
  );
}

function fileExtBadge(doc) {
  const mime = String(doc?.mimeType || '').toLowerCase();
  const name = String(doc?.fileName || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (mime.includes('png') || name.endsWith('.png')) return 'PNG';
  if (mime.includes('webp') || name.endsWith('.webp')) return 'WEBP';
  if (mime.includes('gif') || name.endsWith('.gif')) return 'GIF';
  if (mime.includes('jpeg') || mime.includes('jpg') || /\.jpe?g$/.test(name)) return 'JPG';
  if (doc?.kind === 'pdf') return 'PDF';
  if (doc?.kind === 'image') return 'IMG';
  return 'FILE';
}

function DocumentCard({ doc, onOpen }) {
  const isImage = doc.kind === 'image' && !!doc.url;
  const isPdf = doc.kind === 'pdf';

  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="100%"
      overflow="hidden"
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      cursor="pointer"
      _hover={{ borderColor: 'utkarsh.amber' }}
      onClick={() => onOpen?.(doc)}
    >
      {isImage ? (
        <Box
          w="100%"
          h={{ base: '140px', xl: '160px' }}
          bg="blackAlpha.50"
          overflow="hidden"
        >
          <Image
            src={doc.url}
            alt={doc.label || doc.fileName || 'Document'}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        </Box>
      ) : null}
      <Flex align="center" gap={3} p={{ base: 3, xl: 4 }}>
        {!isImage ? (
          <Flex
            w={{ base: '44px', xl: '52px' }}
            h={{ base: '44px', xl: '52px' }}
            align="center"
            justify="center"
            borderRadius="sm"
            bg={isPdf ? 'whiteAlpha.100' : 'bg.amberTint'}
            flexShrink={0}
          >
            <Icon
              as={isPdf ? MdDescription : MdInsertDriveFile}
              boxSize={{ base: 6, xl: 7 }}
              color="text.secondary"
            />
          </Flex>
        ) : null}
        <VStack align="start" spacing={0} minW={0} flex={1}>
          <Text fontSize={{ base: '14px', xl: '15px' }} fontWeight="600" noOfLines={1}>
            {doc.label}
          </Text>
          <Text
            fontSize={{ base: '12px', xl: '13px' }}
            color="text.secondary"
            noOfLines={1}
            fontFamily="mono"
          >
            {doc.fileName}
          </Text>
        </VStack>
        <StatusBadge status="neutral">{fileExtBadge(doc)}</StatusBadge>
      </Flex>
    </Box>
  );
}

function fileKind(file) {
  const name = (file?.fileName || file?.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  return 'image';
}

function toDoc(id, label, file) {
  if (!file) return null;
  return {
    id,
    label,
    fileName: file.fileName || file.name || 'attachment',
    kind: file.kind || fileKind(file),
  };
}

export default function VendorDetailPage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const editModal = useDisclosure();
  const statusModal = useDisclosure();
  const assetModal = useDisclosure();
  const requestModal = useDisclosure();
  const fileViewer = useDisclosure();
  const [viewerFile, setViewerFile] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [portalToken, setPortalToken] = useState('');
  const [portalBusy, setPortalBusy] = useState(false);
  const [villages, setVillages] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [vendorAssets, setVendorAssets] = useState([]);
  const [vendorIndents, setVendorIndents] = useState([]);
  const [apiDocs, setApiDocs] = useState([]);
  const [indenterFormEnabled, setIndenterFormEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [assignBuId, setAssignBuId] = useState('');
  const [buBusy, setBuBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loaded, villageRes, buRes, siteRes, configIndenter] = await Promise.all([
        loadByIdOrCode({
          param: vendorId,
          listFn: listVendors,
          getFn: getVendor,
          codeKey: 'vendorId',
        }),
        lookupVillages(),
        lookupBusinessUnits(),
        lookupSites(),
        getConfigValue('indenterFormEnabled'),
      ]);
      setVendor(loaded);
      if (loaded?.portalToken) setPortalToken(loaded.portalToken);
      else if (loaded?.verificationStatus !== 'Verified') setPortalToken('');
      setVillages(villageRes || []);
      setBusinessUnits(buRes || []);
      setSites(siteRes || []);
      setIndenterFormEnabled(configIndenter !== false);
      if (loaded?.id) {
        const [assetRes, indentRes, docs] = await Promise.all([
          listEquipment({ pageSize: 200, ownerType: 'vendor', vendorId: loaded.id }),
          listIndents({ pageSize: 200, vendorId: loaded.id }),
          listDocuments({ ownerType: 'vendor', ownerId: loaded.id }).catch(() => []),
        ]);
        setVendorAssets(assetRes.items || []);
        setVendorIndents(indentRes.items || []);
        setApiDocs(Array.isArray(docs) ? docs : []);
      } else {
        setVendorAssets([]);
        setVendorIndents([]);
        setApiDocs([]);
      }
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load vendor',
        status: 'error',
        position: 'top-right',
      });
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, [vendorId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedBuIds = useMemo(
    () => new Set((vendor?.businessUnits || []).map((a) => Number(a.buId))),
    [vendor],
  );

  const assignableBuOptions = useMemo(
    () =>
      (businessUnits || [])
        .filter((b) => b.active !== false && !assignedBuIds.has(Number(b.id)))
        .map((b) => ({
          value: String(b.id),
          label: b.shortName || b.code || b.buId,
        })),
    [businessUnits, assignedBuIds],
  );

  const handleAssignBu = async () => {
    if (!vendor?.id || !assignBuId) return;
    setBuBusy(true);
    try {
      const updated = await assignVendorBu(vendor.id, {
        buId: Number(assignBuId),
        isPrimary: !(vendor.businessUnits || []).length,
      });
      setVendor(updated);
      setAssignBuId('');
      toast({
        title: 'Business unit assigned',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to assign BU',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBuBusy(false);
    }
  };

  const handleRevokeBu = async (buId) => {
    if (!vendor?.id || buId == null) return;
    setBuBusy(true);
    try {
      const updated = await revokeVendorBu(vendor.id, buId);
      setVendor(updated);
      toast({
        title: 'Business unit revoked',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to revoke BU',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBuBusy(false);
    }
  };

  const village = vendor ? findVillageById(villages, vendor.villageId) : null;
  const bu = vendor ? findBuById(businessUnits, vendor.buId) : null;
  const site = vendor ? findSiteById(sites, vendor.siteId) : null;

  const {
    sorted: sortedAssets,
    sort: assetSort,
    toggleSort: toggleAssetSort,
  } = useTableSort(vendorAssets, {
    assetId: (a) => a.assetId,
    description: (a) => a.equipmentDescription || a.equipmentType,
    type: (a) => a.equipmentType,
    makeModel: (a) => [a.manufacturer, a.model].filter(Boolean).join(' '),
    regNo: (a) => a.regNo,
    capacity: (a) => a.capacity,
    kyc: (a) => a.status || 'Pending',
    operational: (a) => a.operationalStatus || 'Available',
  });

  const {
    sorted: sortedIndents,
    sort: indentSort,
    toggleSort: toggleIndentSort,
  } = useTableSort(vendorIndents, {
    indentId: (i) => i.indentId,
    equipment: (i) => i.line.equipmentType,
    period: (i) => i.line.requiredFrom,
    recs: (i) => i.recommendations?.length || 0,
    status: (i) => i.status,
  });

  const verificationStatus = vendor?.verificationStatus || 'Pending';
  const verificationBadge =
    verificationStatus === 'Verified'
      ? 'verified'
      : verificationStatus === 'Rejected' || verificationStatus === 'Blocked'
        ? 'error'
        : 'pending';

  const kycDocs = useMemo(() => {
    if (apiDocs.length) {
      const labels = {
        profile_photo: 'Profile / Logo',
        gst_certificate: 'GST certificate',
        office_photo: 'Business / office photo',
        aadhaar_front: 'Aadhaar — Front',
        aadhaar_back: 'Aadhaar — Back',
        pan_card: 'PAN card',
      };
      return apiDocs.map((d) => ({
        id: d.id,
        docType: d.docType || '',
        label: labels[d.docType] || d.docType || 'Document',
        fileName: d.fileName || 'attachment',
        mimeType: d.mimeType || '',
        kind: (d.mimeType || '').includes('pdf') ? 'pdf' : 'image',
        url: d.url,
      }));
    }
    if (!vendor) return [];
    return [
      toDoc('gst', 'GST certificate', vendor.gstCertificate),
      toDoc('office', 'Business / office photo', vendor.businessOfficePhoto),
      toDoc('aadhaar-front', 'Aadhaar — Front', vendor.aadhaarFront),
      toDoc('aadhaar-back', 'Aadhaar — Back', vendor.aadhaarBack),
      toDoc('pan', 'PAN card', vendor.panCard),
      toDoc('photo', 'Profile / Logo', vendor.photo),
    ]
      .filter(Boolean)
      .map((d) => ({
        ...d,
        docType: d.id === 'photo' ? 'profile_photo' : d.id,
      }));
  }, [vendor, apiDocs]);

  const profileDoc = useMemo(
    () =>
      kycDocs.find((d) => d.docType === 'profile_photo' || d.label === 'Profile / Logo') || null,
    [kycDocs],
  );

  const attachmentDocs = useMemo(
    () =>
      kycDocs.filter(
        (d) => d.docType !== 'profile_photo' && d.label !== 'Profile / Logo',
      ),
    [kycDocs],
  );

  const refreshVendor = (extra = {}) => {
    if (extra?.portalToken) setPortalToken(extra.portalToken);
    return load();
  };

  const handleRegeneratePortal = async () => {
    if (!vendor?.id) return;
    setPortalBusy(true);
    try {
      const updated = await regenerateVendorPortalToken(vendor.id);
      if (updated?.portalToken) setPortalToken(updated.portalToken);
      setVendor((v) =>
        v
          ? {
              ...v,
              ...updated,
              portalToken: updated.portalToken || v.portalToken,
            }
          : updated,
      );
      toast({
        title: 'Portal link ready',
        description: 'Copy and share this link with the vendor.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to generate portal link',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setPortalBusy(false);
    }
  };

  const openDoc = (doc) => {
    if (!doc?.url) {
      toast({
        title: doc?.label || 'Document',
        description: doc?.fileName || 'No preview URL',
        status: 'info',
        position: 'top-right',
        duration: 2500,
      });
      return;
    }
    setViewerFile(doc);
    fileViewer.onOpen();
  };

  if (loading) {
    return <PageLoader label="Loading vendor…" />;
  }

  if (!vendor) {
    return (
      <Box>
        <OutlineButton
          size="sm"
          leftIcon={<MdArrowBack />}
          mb={4}
          onClick={() => navigate('/admin/equipments/vendors')}
        >
          Back to vendors
        </OutlineButton>
        <AppCard py={12} textAlign="center">
          <Text fontWeight="600" fontSize={{ base: '16px', xl: '18px' }}>
            Vendor not found
          </Text>
          <Text mt={2} color="text.secondary" fontSize={{ base: '14px', xl: '15px' }}>
            No Vendor Master record for {vendorId}.
          </Text>
        </AppCard>
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <OutlineButton
            size="sm"
            leftIcon={<MdArrowBack />}
            mb={3}
            onClick={() => navigate('/admin/equipments/vendors')}
          >
            Back
          </OutlineButton>
          <Flex align="center" gap={{ base: 3, md: 4 }}>
            <Box
              as="button"
              type="button"
              flexShrink={0}
              onClick={() => (profileDoc?.url ? openDoc(profileDoc) : undefined)}
              cursor={profileDoc?.url ? 'pointer' : 'default'}
              borderRadius="md"
              overflow="hidden"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.input"
              w={{ base: '64px', md: '80px' }}
              h={{ base: '64px', md: '80px' }}
              _hover={profileDoc?.url ? { borderColor: 'utkarsh.amber' } : undefined}
              aria-label="View profile logo"
            >
              {profileDoc?.url ? (
                <Image
                  src={profileDoc.url}
                  alt={vendor.tradeAgencyName || 'Profile'}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              ) : (
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Icon as={MdImage} boxSize={7} color="text.secondary" />
                </Flex>
              )}
            </Box>
            <Box minW={0}>
              <Heading size={{ base: 'lg', xl: 'xl' }} noOfLines={2}>
                {vendor.tradeAgencyName}
              </Heading>
              <Flex align="center" gap={2} mt={2} wrap="wrap">
                <Text
                  color="text.secondary"
                  fontFamily="mono"
                  fontSize={{ base: '14px', xl: '15px' }}
                >
                  {vendor.vendorId}
                </Text>
                <StatusBadge status={verificationBadge}>{verificationStatus}</StatusBadge>
                <ActiveBadge active={vendor.active} />
                {bu || site || (vendor.businessUnits || []).length ? (
                  <Text
                    fontSize={{ base: '13px', xl: '14px' }}
                    fontWeight="600"
                    color="text.primary"
                    px={3}
                    py={1}
                    bg="bg.input"
                    borderRadius="full"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    {[
                      (vendor.businessUnits || [])
                        .map((a) => a.shortName || a.buId)
                        .filter(Boolean)
                        .join(', ') ||
                        bu?.shortName ||
                        vendor.buId,
                      site?.siteName || vendor.siteId,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                ) : null}
                {vendor.kronosNumber ? (
                  <Text
                    fontSize={{ base: '13px', xl: '14px' }}
                    color="text.secondary"
                    px={3}
                    py={1}
                    bg="bg.amberTint"
                    borderRadius="full"
                    borderWidth="1px"
                    borderColor="utkarsh.amber"
                  >
                    Kronos:{' '}
                    <Text as="span" fontFamily="mono" fontWeight="600" color="text.primary">
                      {vendor.kronosNumber}
                    </Text>
                  </Text>
                ) : null}
              </Flex>
              {bu?.fullNameGst || site?.projectOperation ? (
                <Text mt={2} fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
                  {[bu?.fullNameGst, site?.projectOperation].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
            </Box>
          </Flex>
        </Box>
        <HStack spacing={2} align="start" wrap="wrap">
          {verificationStatus === 'Verified' && indenterFormEnabled ? (
            <OutlineButton
              size="sm"
              leftIcon={<MdAssignment />}
              onClick={requestModal.onOpen}
            >
              Indenter Form / Request
            </OutlineButton>
          ) : null}
          <OutlineButton
            size="sm"
            leftIcon={<MdVerifiedUser />}
            onClick={statusModal.onOpen}
          >
            Update status
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdEdit />} onClick={editModal.onOpen}>
            Edit
          </PrimaryButton>
        </HStack>
      </Flex>

      {verificationStatus === 'Rejected' && vendor.rejectionReason ? (
        <Box mb={6}>
          <StatusAlertCard
            variant="error"
            title="Rejected"
            body={vendor.rejectionReason}
            footer="Update status to change verification or clear this reason."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      {verificationStatus === 'Blocked' && vendor.blockReason ? (
        <Box mb={6}>
          <StatusAlertCard
            variant="error"
            title="Blocked"
            body={vendor.blockReason}
            footer="Request link is disabled. Verify again to generate a new link."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      {verificationStatus === 'Pending' ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Verification pending"
            body="This vendor is awaiting review. Use Update status to Verify or Reject."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      {verificationStatus === 'Verified' ? (
        <AppCard mb={6}>
          <Heading size={{ base: 'sm', xl: 'md' }} mb={1}>
            Vendor portal link
          </Heading>
          <Text fontSize="13px" color="text.secondary" mb={3}>
            Share this link with the vendor so they can raise requests. The full URL is only shown
            when generated — use Generate / Regenerate if you need it again.
          </Text>
          {portalToken ? (
            <Box
              p={3}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
              fontFamily="mono"
              fontSize="13px"
              wordBreak="break-all"
              mb={3}
            >
              {vendorPortalUrl(portalToken)}
            </Box>
          ) : (
            <Box
              p={3}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
              mb={3}
            >
              <Text fontSize="14px" color="text.secondary">
                {vendor.portalTokenIssuedAt
                  ? 'A portal link was issued earlier, but the secret token is not stored for redisplay. Generate a new link to copy it.'
                  : 'No portal link yet. Generate one to share with this vendor.'}
              </Text>
            </Box>
          )}
          <HStack spacing={2} wrap="wrap">
            {portalToken ? (
              <>
                <OutlineButton
                  size="sm"
                  leftIcon={<MdContentCopy />}
                  onClick={() => {
                    const url = vendorPortalUrl(portalToken);
                    navigator.clipboard?.writeText(url);
                    toast({
                      title: 'Link copied',
                      status: 'success',
                      position: 'top-right',
                    });
                  }}
                >
                  Copy link
                </OutlineButton>
                <OutlineButton
                  size="sm"
                  leftIcon={<MdOpenInNew />}
                  onClick={() =>
                    window.open(vendorPortalUrl(portalToken), '_blank', 'noopener,noreferrer')
                  }
                >
                  Open portal
                </OutlineButton>
              </>
            ) : null}
            <PrimaryButton size="sm" onClick={handleRegeneratePortal} isLoading={portalBusy}>
              {portalToken || vendor.portalTokenIssuedAt ? 'Regenerate link' : 'Generate link'}
            </PrimaryButton>
          </HStack>
        </AppCard>
      ) : null}

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={2}>
          Business Units
        </Heading>
        <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mb={4}>
          Assign or revoke BUs for this agency. Agencies cannot pick BUs at signup.
        </Text>
        <Flex gap={2} wrap="wrap" mb={4} minH="32px">
          {(vendor.businessUnits || []).length === 0 ? (
            <Text fontSize="14px" color="text.secondary">
              No business units assigned yet.
            </Text>
          ) : (
            (vendor.businessUnits || []).map((a) => (
              <Tag
                key={a.buId}
                size="lg"
                borderRadius="full"
                variant="subtle"
                colorScheme={a.isPrimary ? 'orange' : 'gray'}
              >
                <TagLabel>
                  {a.shortName || a.buId}
                  {a.isPrimary ? ' · Primary' : ''}
                </TagLabel>
                <TagCloseButton
                  isDisabled={buBusy}
                  onClick={() => handleRevokeBu(a.buId)}
                />
              </Tag>
            ))
          )}
        </Flex>
        <HStack align="end" spacing={3} wrap="wrap">
          <Box minW={{ base: '100%', sm: '260px' }} flex="1">
            <AppSelect
              label="Assign business unit"
              placeholder="Select BU"
              value={assignBuId}
              options={assignableBuOptions}
              onChange={setAssignBuId}
              isSearchable
              isDisabled={buBusy || assignableBuOptions.length === 0}
            />
          </Box>
          <PrimaryButton
            size="sm"
            leftIcon={<MdAdd />}
            onClick={handleAssignBu}
            isLoading={buBusy}
            isDisabled={!assignBuId}
            mb={1}
          >
            Assign
          </PrimaryButton>
        </HStack>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Business details
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Trade / Agency Name" value={vendor.tradeAgencyName} />
          <DetailRow label="GST Number" value={vendor.gstNumber} />
          <DetailRow label="Constitution" value={vendor.constitution} />
          <DetailRow label="LOCAL / PROFESSIONAL" value={vendor.localProfessional} />
          <DetailRow label="Major Category" value={vendor.majorCategory} />
          <DetailRow
            label="Date of Incorporation"
            value={formatDobDisplay(vendor.dateOfIncorporation)}
          />
          <DetailRow label="Verification" value={verificationStatus} />
          <DetailRow label="Active" value={vendor.active ? 'Active' : 'Inactive'} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Owner / Partner
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Owner Name" value={vendor.ownerName} />
          <DetailRow label="Father’s / Husband’s Name" value={vendor.fatherHusbandName} />
          <DetailRow label="Surname" value={vendor.surname} />
          <DetailRow label="Full Name" value={vendor.ownerFullName} />
          <DetailRow label="Mobile" value={vendor.mobile} />
          <DetailRow label="Email" value={vendor.email} />
          <DetailRow label="Aadhaar" value={vendor.aadhaarNumber} />
          <DetailRow label="PAN" value={vendor.pan} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Address
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="House" value={vendor.address1} />
          <DetailRow label="Street" value={vendor.address2} />
          <DetailRow label="Village" value={village?.villageName} />
          <DetailRow label="PIN" value={vendor.pin || village?.pin} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6} p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Flex justify="space-between" align="start" gap={3} wrap="wrap">
            <Box>
              <Heading size={{ base: 'sm', xl: 'md' }}>Vendor equipment</Heading>
              <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mt={1}>
                Fleet owned by this agency. Status shows Available or Deployed on a request.
              </Text>
            </Box>
            <OutlineButton size="sm" leftIcon={<MdAdd />} onClick={assetModal.onOpen}>
              Add equipment
            </OutlineButton>
          </Flex>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="assetId" sort={assetSort} onSort={toggleAssetSort}>
                  Asset
                </SortableTh>
                <SortableTh
                  columnKey="description"
                  sort={assetSort}
                  onSort={toggleAssetSort}
                >
                  Description
                </SortableTh>
                <SortableTh columnKey="type" sort={assetSort} onSort={toggleAssetSort}>
                  Type
                </SortableTh>
                <SortableTh
                  columnKey="makeModel"
                  sort={assetSort}
                  onSort={toggleAssetSort}
                >
                  Make / Model
                </SortableTh>
                <SortableTh columnKey="regNo" sort={assetSort} onSort={toggleAssetSort}>
                  Reg. No.
                </SortableTh>
                <SortableTh
                  columnKey="capacity"
                  sort={assetSort}
                  onSort={toggleAssetSort}
                >
                  Capacity
                </SortableTh>
                <SortableTh columnKey="kyc" sort={assetSort} onSort={toggleAssetSort}>
                  KYC
                </SortableTh>
                <SortableTh
                  columnKey="operational"
                  sort={assetSort}
                  onSort={toggleAssetSort}
                >
                  Status
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {sortedAssets.map((a) => (
                <Tr
                  key={a.assetId}
                  cursor="pointer"
                  _hover={{ bg: 'bg.input' }}
                  onClick={() =>
                    navigate(
                      `/admin/equipments/vendors/${vendor.vendorId || vendorId}/assets/${a.assetId}`,
                    )
                  }
                >
                  <Td fontFamily="mono" fontSize="13px">
                    {a.assetId}
                  </Td>
                  <Td fontWeight="600">{a.equipmentDescription || a.equipmentType}</Td>
                  <Td fontSize="13px" color="text.secondary">
                    {a.equipmentType}
                  </Td>
                  <Td>
                    {a.manufacturer} {a.model}
                    {a.remarks ? (
                      <Text fontSize="12px" color="text.secondary" mt={0.5} noOfLines={2}>
                        {a.remarks}
                      </Text>
                    ) : null}
                  </Td>
                  <Td fontFamily="mono" fontSize="13px">
                    {a.regNo}
                  </Td>
                  <Td fontSize="13px">
                    {a.capacity ? `${a.capacity} ${a.capacityUom || ''}`.trim() : '—'}
                  </Td>
                  <Td>
                    <StatusBadge status={a.status === 'Verified' ? 'verified' : 'pending'}>
                      {a.status}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <StatusBadge status={operationalBadge(a.operationalStatus)}>
                      {a.operationalStatus || 'Available'}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))}
              {vendorAssets.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <Text color="text.secondary" py={8} textAlign="center">
                      No equipment registered yet. Add equipment if this vendor owns machines.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      <AppCard mb={6} p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Heading size={{ base: 'sm', xl: 'md' }}>Requests from this vendor</Heading>
          <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mt={1}>
            Equipment indents raised for their BU / Site work.
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh
                  columnKey="indentId"
                  sort={indentSort}
                  onSort={toggleIndentSort}
                >
                  Indent
                </SortableTh>
                <SortableTh
                  columnKey="equipment"
                  sort={indentSort}
                  onSort={toggleIndentSort}
                >
                  Equipment
                </SortableTh>
                <SortableTh
                  columnKey="period"
                  sort={indentSort}
                  onSort={toggleIndentSort}
                >
                  Period
                </SortableTh>
                <SortableTh columnKey="recs" sort={indentSort} onSort={toggleIndentSort}>
                  Recs
                </SortableTh>
                <SortableTh
                  columnKey="status"
                  sort={indentSort}
                  onSort={toggleIndentSort}
                >
                  Status
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {sortedIndents.map((ind) => (
                <Tr
                  key={ind.indentId}
                  cursor="pointer"
                  _hover={{ bg: 'bg.input' }}
                  onClick={() => navigate(`/admin/equipments/indents/${ind.indentId}`)}
                >
                  <Td fontFamily="mono" fontSize="13px">
                    {ind.indentId}
                  </Td>
                  <Td fontWeight="600">{ind.line.equipmentType}</Td>
                  <Td fontSize="13px">
                    {formatDobDisplay(ind.line.requiredFrom)} →{' '}
                    {formatDobDisplay(ind.line.requiredTill)}
                  </Td>
                  <Td fontWeight="600">{ind.recommendations?.length || 0}</Td>
                  <Td>
                    <StatusBadge status={indentStatusBadge(ind.status)}>
                      {ind.status}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))}
              {vendorIndents.length === 0 ? (
                <Tr>
                  <Td colSpan={5}>
                    <Text color="text.secondary" py={8} textAlign="center">
                      No requests yet. Use Indenter Form / Request when equipment is
                      needed.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={1}>
          Documents attached
        </Heading>
        <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mb={4}>
          KYC files from vendor signup (GST, office photo, Aadhaar, PAN). Click to preview.
        </Text>
        {attachmentDocs.length > 0 ? (
          <SimpleGrid columns={DOC_COLUMNS} spacing={{ base: 3, xl: 4 }}>
            {attachmentDocs.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} onOpen={openDoc} />
            ))}
          </SimpleGrid>
        ) : (
          <Text
            color="text.secondary"
            py={4}
            textAlign="center"
            fontSize={{ base: '14px', xl: '15px' }}
          >
            No KYC documents attached yet.
          </Text>
        )}
      </AppCard>

      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => {
          fileViewer.onClose();
          setViewerFile(null);
        }}
        file={viewerFile}
      />

      <VendorFormModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        vendor={vendor}
        onSaved={refreshVendor}
      />
      <VendorVerificationStatusModal
        isOpen={statusModal.isOpen}
        onClose={statusModal.onClose}
        vendor={vendor}
        onSaved={refreshVendor}
      />
      <VendorAssetFormModal
        isOpen={assetModal.isOpen}
        onClose={assetModal.onClose}
        vendor={vendor}
        onSaved={refreshVendor}
      />
      <IndentFormModal
        isOpen={requestModal.isOpen}
        onClose={requestModal.onClose}
        presetVendorId={vendor.vendorId}
        presetVendor={vendor}
        onSaved={(indent) => {
          refreshVendor();
          if (indent?.indentId) {
            navigate(`/admin/equipments/indents/${indent.indentId}`);
          }
        }}
      />
    </Box>
  );
}
